import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const SITE = "https://acbothub.com";

const DEFAULT_SNIPPET = `<!-- StoreBot widget by ACBotHub -->
<script async src="${SITE}/storebot-widget.js"
        data-config="TU_EMAIL_DE_CUENTA_ACBOTHUB@ejemplo.cl"
        data-api="${SITE}"
        data-color="#0ea5e9"
        data-greeting="Hola! ¿En qué te ayudo hoy?"></script>`;

const ATTRIBUTES = [
  {
    name: "data-config",
    required: true,
    value: "string (email)",
    desc: "El email con el que iniciaste sesión en ACBotHub. Identifica qué configuración cargar (nombre del negocio, catálogo, API key, etc.).",
    example: 'data-config="tienda@ejemplo.cl"',
  },
  {
    name: "data-api",
    required: true,
    value: "URL absoluta",
    desc: `Base URL de ACBotHub. Casi siempre es ${SITE}.`,
    example: `data-api="${SITE}"`,
  },
  {
    name: "data-color",
    required: false,
    value: "hex color (#rrggbb)",
    desc: "Color del launcher flotante y de los mensajes del cliente. Por defecto es sky (#0ea5e9). Usa el color primario de tu marca.",
    example: 'data-color="#f97316"',
  },
  {
    name: "data-greeting",
    required: false,
    value: "string",
    desc: "Primer mensaje que muestra el bot al abrir el chat. Si lo omites, usa un saludo genérico.",
    example: 'data-greeting="Hola! Bienvenido a Casa Diseño. ¿Qué producto buscas?"',
  },
];

const FAQ = [
  {
    q: "¿Cómo instalo el widget en un sitio HTML plano?",
    a: (
      <>
        <p>Copia el snippet del dashboard y pégalo justo antes del cierre de la etiqueta <code className="bg-gray-100 px-1 rounded text-xs">&lt;/body&gt;</code> en tu archivo HTML. El widget se auto-inyecta al cargar la página.</p>
      </>
    ),
  },
  {
    q: "¿Cómo lo instalo en WordPress?",
    a: (
      <>
        <p className="mb-2">Tienes 3 opciones, ordenadas de más fácil a más avanzada:</p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm">
          <li>
            <strong>Plugin &ldquo;Insert Headers and Footers&rdquo;</strong> (o &ldquo;WPCode&rdquo;): instálalo desde el menú Plugins → Añadir nuevo. Luego en Ajustes → &ldquo;Insert Headers and Footers&rdquo; → pega el snippet en la caja <em>Scripts in Footer</em> y guarda.
          </li>
          <li>
            <strong>Elementor / Divi / cualquier constructor</strong>: la mayoría tiene un módulo &ldquo;Código HTML&rdquo; o &ldquo;Custom HTML&rdquo;. Pega el snippet en ese módulo, sitúalo en el footer del sitio.
          </li>
          <li>
            <strong>Directo en el tema</strong>: edita <code className="bg-gray-100 px-1 rounded text-xs">footer.php</code> del tema hijo y pega el snippet justo antes de <code className="bg-gray-100 px-1 rounded text-xs">&lt;/body&gt;</code>. Solo recomendado si sabes de PHP.
          </li>
        </ol>
      </>
    ),
  },
  {
    q: "¿Cómo lo instalo en Shopify?",
    a: (
      <>
        <p className="mb-2">Shopify permite inyectar scripts en el layout global sin editar código Liquid manual:</p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm">
          <li>Panel de Shopify → <strong>Online Store</strong> → <strong>Themes</strong>.</li>
          <li>En el tema activo → <strong>Actions</strong> → <strong>Edit code</strong>.</li>
          <li>En la carpeta <strong>Layout</strong> abre el archivo <code className="bg-gray-100 px-1 rounded text-xs">theme.liquid</code>.</li>
          <li>Busca la etiqueta <code className="bg-gray-100 px-1 rounded text-xs">&lt;/body&gt;</code> (usa Ctrl+F).</li>
          <li>Pega el snippet <strong>justo antes</strong> de esa etiqueta y guarda.</li>
        </ol>
        <p className="mt-2 text-xs text-gray-500">Ver preview en modo incognito para asegurarte de que carga sin depender de caché de admin.</p>
      </>
    ),
  },
  {
    q: "¿Cómo lo instalo en Wix?",
    a: (
      <>
        <p className="mb-2">Wix requiere plan Business/Ecommerce para inyectar código custom:</p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm">
          <li>Panel Wix → <strong>Settings</strong> → <strong>Custom Code</strong> (Advanced).</li>
          <li><strong>+ Add Custom Code</strong>. Pega el snippet completo.</li>
          <li>Nombre: &ldquo;StoreBot&rdquo;. Location: <strong>Body — end</strong>. Apply to: <strong>All pages</strong>. Load code once: sí.</li>
          <li>Guarda y publica el sitio.</li>
        </ol>
      </>
    ),
  },
  {
    q: "¿Cómo cambio el color del launcher?",
    a: (
      <p>Modifica el atributo <code className="bg-gray-100 px-1 rounded text-xs">data-color</code> con cualquier hex de tu marca. Ejemplos: <code>#f97316</code> (naranja), <code>#10b981</code> (verde), <code>#7c3aed</code> (violeta), <code>#111827</code> (negro). El launcher circular, el header del chat y los mensajes del cliente se colorean con este valor automáticamente.</p>
    ),
  },
  {
    q: "¿Cómo cambio el mensaje de bienvenida?",
    a: (
      <p>Modifica el atributo <code className="bg-gray-100 px-1 rounded text-xs">data-greeting</code>. Ejemplo: <code className="bg-gray-100 px-1 rounded text-xs">{'data-greeting="¡Hola! Soy Sofía de Casa Diseño. Cuéntame qué buscas."'}</code></p>
    ),
  },
  {
    q: "¿El widget funciona en móvil?",
    a: (
      <p>Sí. El launcher es responsive: en pantallas chicas el panel del chat ocupa hasta <code className="bg-gray-100 px-1 rounded text-xs">calc(100vw - 40px)</code> de ancho y <code className="bg-gray-100 px-1 rounded text-xs">calc(100vh - 120px)</code> de alto para no cubrir toda la pantalla y dejar el launcher visible al cerrarlo.</p>
    ),
  },
  {
    q: "¿Puedo poner el launcher en la esquina izquierda?",
    a: (
      <>
        <p className="mb-2">Por defecto va abajo a la derecha. Para moverlo, agrega este CSS en tu sitio <em>después</em> del snippet:</p>
        <pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-x-auto"><code>{`<style>
  [data-storebot-widget] { right: auto !important; left: 20px !important; }
  [data-storebot-widget] > div { right: auto !important; left: 0 !important; }
</style>`}</code></pre>
      </>
    ),
  },
  {
    q: "¿Cómo sé si el bot capturó un lead?",
    a: (
      <p>Cuando el bot detecta que el cliente entregó nombre + teléfono + email, guarda el lead y envía un email inmediato a la dirección configurada en <strong>notification_email</strong> del dashboard. Además puedes ver todos los leads capturados en <Link to="/kit-store" className="text-sky-600 hover:underline">Dashboard → Kit StoreBot → tab Leads</Link>.</p>
    ),
  },
  {
    q: "¿El bot puede recomendar productos que no están en mi catálogo?",
    a: (
      <p>No. El prompt del bot le prohíbe inventar precios, productos o especificaciones que no estén en tu <strong>knowledge_base</strong>. Si el cliente pregunta por algo que no cargaste, el bot ofrece derivar al equipo de ventas por WhatsApp o email. Por eso: <strong>mientras más completo y actualizado tu catálogo, mejor</strong>.</p>
    ),
  },
  {
    q: "¿Cuánto cobra OpenAI por cada conversación?",
    a: (
      <p>StoreBot usa <code className="bg-gray-100 px-1 rounded text-xs">gpt-4o-mini</code>, uno de los modelos más económicos de OpenAI. Una conversación promedio (7-10 mensajes) cuesta menos de <strong>USD $0.01</strong>. Cargas $5 en tu cuenta de OpenAI y te alcanza para ~500 conversaciones. Recomendamos configurar un <a href="https://platform.openai.com/settings/organization/billing/limits" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">límite de gasto mensual</a> por seguridad.</p>
    ),
  },
];


export default function StoreBotDocsPage() {
  const [copied, setCopied] = useState(false);

  const copySnippet = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(DEFAULT_SNIPPET);
      } else {
        // Fallback for older browsers or contexts without clipboard permission.
        const ta = document.createElement("textarea");
        ta.value = DEFAULT_SNIPPET;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Silently fail — user can still select the snippet manually.
    }
  };

  return (
    <>
      <SEO
        title="Documentación del widget StoreBot — ACBotHub"
        description="Cómo instalar el widget de StoreBot en tu sitio web: WordPress, Shopify, Wix o HTML plano. Data attributes, personalización y FAQ."
        path="/docs/storebot-widget"
      />
      <div className="min-h-screen bg-gray-50">
        {/* Nav */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="text-lg font-bold text-gray-950" data-testid="docs-back-home">
              ACBotHub
            </Link>
            <Link
              to="/kits/storebot"
              className="text-sm text-gray-600 hover:text-sky-600 transition-colors"
              data-testid="docs-back-kit"
            >
              ← Volver al kit StoreBot
            </Link>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Hero */}
          <div className="mb-10" data-testid="docs-hero">
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-xs font-semibold mb-3">
              🛍️ Documentación
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-950 mb-3">
              Widget de StoreBot — Guía de instalación
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Instala StoreBot en cualquier sitio web en 2 minutos. Se ve como un botón flotante estilo WhatsApp y se abre en un chat conversacional que responde 24/7 desde tu catálogo.
            </p>
          </div>

          {/* Snippet */}
          <section className="mb-12" data-testid="docs-snippet-section">
            <h2 className="text-xl font-bold text-gray-950 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
              Copia el snippet
            </h2>
            <p className="text-gray-600 mb-4 ml-9">
              Reemplaza <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">TU_EMAIL_DE_CUENTA_ACBOTHUB@ejemplo.cl</code> por el email con el que iniciaste sesión en ACBotHub. El resto puedes dejarlo tal cual o personalizar (ver siguiente sección).
            </p>
            <div className="ml-9 relative">
              <pre
                data-testid="docs-snippet"
                className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto pr-24"
              ><code>{DEFAULT_SNIPPET}</code></pre>
              <button
                onClick={copySnippet}
                data-testid="docs-copy-btn"
                className="absolute top-3 right-3 bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
              >
                {copied ? "✓ Copiado" : "📋 Copiar"}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3 ml-9">
              💡 Sugerencia: puedes obtener el snippet ya personalizado con tu email desde{" "}
              <Link to="/kit-store" className="text-sky-600 hover:underline">Dashboard → Kit StoreBot → Ver snippet</Link>.
            </p>
          </section>

          {/* Data attributes */}
          <section className="mb-12" data-testid="docs-attributes-section">
            <h2 className="text-xl font-bold text-gray-950 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
              Personaliza (opcional)
            </h2>
            <p className="text-gray-600 mb-4 ml-9">
              El snippet acepta cuatro <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">data-*</code> attributes:
            </p>
            <div className="ml-9 overflow-x-auto">
              <table className="w-full text-sm bg-white rounded-lg border border-gray-200 overflow-hidden">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Atributo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Requerido</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Descripción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {ATTRIBUTES.map((a) => (
                    <tr key={a.name} className="hover:bg-gray-50" data-testid={`docs-attr-${a.name}`}>
                      <td className="px-4 py-3 align-top">
                        <code className="text-sky-700 font-mono text-xs">{a.name}</code>
                        <div className="text-[10px] text-gray-400 mt-1">{a.value}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {a.required ? (
                          <span className="inline-block bg-rose-100 text-rose-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">Sí</span>
                        ) : (
                          <span className="inline-block bg-gray-100 text-gray-600 text-[11px] font-semibold px-2 py-0.5 rounded-full">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-sm">
                        <div>{a.desc}</div>
                        <div className="mt-1 text-[11px] text-gray-500 font-mono">{a.example}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Pega y publica */}
          <section className="mb-12" data-testid="docs-install-section">
            <h2 className="text-xl font-bold text-gray-950 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 bg-sky-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
              Pega en tu sitio y publica
            </h2>
            <p className="text-gray-600 mb-4 ml-9">
              Pega el snippet en tu sitio web antes del cierre de <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">&lt;/body&gt;</code>. Los pasos exactos dependen de la plataforma que uses — mira las preguntas frecuentes más abajo.
            </p>
            <div className="ml-9 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <span className="text-emerald-600 text-lg">✓</span>
                <div>
                  <div className="font-semibold text-emerald-900 text-sm mb-1">Ya está</div>
                  <p className="text-emerald-800 text-sm leading-relaxed">
                    El launcher flotante debería aparecer en la esquina inferior derecha de tu sitio al recargar la página. Al hacer click se abre el chat. Los leads capturados llegan por email a la dirección que configuraste, y quedan guardados en el dashboard.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section data-testid="docs-faq-section">
            <h2 className="text-xl font-bold text-gray-950 mb-6">Preguntas frecuentes</h2>
            <div className="space-y-3">
              {FAQ.map((item, i) => (
                <details
                  key={i}
                  className="group bg-white border border-gray-200 rounded-lg overflow-hidden"
                  data-testid={`docs-faq-${i}`}
                >
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between text-sm font-semibold text-gray-950 hover:bg-gray-50">
                    <span>{item.q}</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="px-5 pb-5 pt-1 text-sm text-gray-700 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Footer CTA */}
          <div className="mt-16 pt-8 border-t border-gray-200 text-center" data-testid="docs-footer-cta">
            <p className="text-gray-600 mb-4">¿Necesitas ayuda con la instalación?</p>
            <a
              href="mailto:soporte@acbothub.com"
              className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
              data-testid="docs-contact-btn"
            >
              📧 Contactar soporte
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
